import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { GenerateJWTPayload, initializeWithConfig, trackPurchase, track } from '@iterable/web-sdk';

interface ProductToTrack {
  id: string;
  name: string;
  price: number;
  quantity: number;
}

@Injectable({
  providedIn: 'root'
})
export class IterableService {

  private isBrowser: boolean;
  private publishableApiKey = environment.iterablePublishableApiKey;

  private sdk: {
    setUserID: (userId: string) => Promise<string>;
    trackPurchase: typeof trackPurchase;
    track: typeof track;
  } | null = null;

  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.init();
  }

  private init(): void {
    if (!this.isBrowser || this.sdk) return;

    const getJwtToken = async (payload: GenerateJWTPayload): Promise<string | undefined> => {
      try {
        const userId = payload.userID;
        if (!userId) {
          console.error('[IterableService] getJwtToken called, but no userId was provided.');
          return undefined;
        }

        const url = `${environment.apiUrl}/api/v2/email/iterable/token?userId=${userId}`;
        const response = await lastValueFrom(
          this.http.get<{ token: string }>(url)
        );

        return response.token;
      } catch (error) {
        console.error('[IterableService] JWT fetch failed:', error);
        return undefined;
      }
    };

    const { setUserID } = initializeWithConfig({
      authToken: this.publishableApiKey,
      configOptions: {
        isEuIterableService: true
      },
      generateJWT: getJwtToken
    });

    this.sdk = {
      setUserID,
      trackPurchase,
      track
    };
  }

  /**
   * Identifies the user to Iterable.
   */
  private async identifyUser(userId: number): Promise<void> {
    if (!this.isBrowser || !this.sdk) return;

    try {
      await this.sdk.setUserID(userId.toString());
    } catch (error) {
      console.error('[IterableService] identification error:', error);
    }
  }

  /**
   * Track a custom event (non-purchase).
   */
  async trackEvent(userId: number, data: string) {
    if (!this.isBrowser || !this.sdk) return;

    try {
      await this.identifyUser(userId);
      await this.sdk.track({eventName: data});
    } catch (error) {
      console.error('[IterableService] trackEvent error:', error);
    }
  }

  /**
   * Track purchase on Iterable.
   */
  async trackConversion(userId: number, userEmail: string, items: ProductToTrack[], total: number) {
    if (!this.isBrowser || !this.sdk) return;

    try {
      await this.identifyUser(userId);
      await this.sdk.trackPurchase({
        items,
        total,
        dataFields: {
          purchase_email: userEmail
        }
      });
    } catch (error) {
      console.error('Iterable trackPurchase error:', error);
    }
  }

}
